import { Injectable } from '@angular/core';
import { FeatureInfoPanelData, FeatureInfoPanelRow } from '../visor.types';

@Injectable({ providedIn: 'root' })
export class FeatureInfoPanelDataService {
  create(
    featureProperties: Record<string, unknown>[],
    isIrrigatorAccess: boolean,
    isOwnIrrigatorParcel = false
  ): FeatureInfoPanelData | undefined {
    if (!featureProperties.length) {
      return undefined;
    }

    const cadastralReference = this.getFirstPropertyText(featureProperties, [
      'referenciacatastral',
      'referencia_catastral',
      'ref_catastral',
      'refcat',
      'ref_cat',
      'referencia',
      'cadastral_reference',
      'cadastralreference',
      'rc'
    ]);
    const surfaceLines = this.getSurfaceLines(featureProperties);
    const take = this.getFirstPropertyText(featureProperties, [
      'toma_asociada',
      'toma',
      'numero_toma',
      'num_toma',
      'n_toma',
      'id_toma',
      'cod_toma',
      'toma_numero',
      'nom_toma'
    ]);
    const rows: FeatureInfoPanelRow[] = [];

    if (surfaceLines.length) {
      rows.push({
        label: 'Superficie',
        value: surfaceLines[0],
        valueLines: surfaceLines,
        kind: 'surface'
      });
    }

    if (take) {
      rows.push({
        label: 'Toma asociada',
        value: this.formatTakeText(take),
        kind: 'take'
      });
    }

    if (isIrrigatorAccess) {
      const irrigationStatus = this.getIrrigationStatus(featureProperties);

      if (irrigationStatus) {
        rows.push({
          label: 'Estado del riego',
          value: irrigationStatus.label,
          kind: 'irrigation-status',
          badge: irrigationStatus.badge
        });
      }
    }

    if (!cadastralReference && !rows.length) {
      return undefined;
    }

    return {
      title: isIrrigatorAccess && isOwnIrrigatorParcel ? 'Mi parcela' : 'Parcelario',
      cadastralReference: cadastralReference || 'Sin referencia',
      rows,
      profile: isIrrigatorAccess ? 'irrigators' : 'public'
    };
  }

  private getSurfaceLines(featureProperties: Record<string, unknown>[]): string[] {
    return [
      this.formatHectares(this.getFirstPropertyText(featureProperties, [
        'superficie_ha',
        'sup_ha',
        'area_ha',
        'hectareas',
        'hectarea',
        'ha',
        'superficie'
      ])),
      this.formatMeasuredValue(this.getFirstPropertyText(featureProperties, ['hanegadas']), 'hanegadas'),
      this.formatMeasuredValue(this.getFirstPropertyText(featureProperties, ['cuartones']), 'cuartones'),
      this.formatMeasuredValue(this.getFirstPropertyText(featureProperties, ['brazas']), 'brazas')
    ].filter((value) => Boolean(value) && !this.isZeroMeasuredValue(value));
  }

  private getIrrigationStatus(featureProperties: Record<string, unknown>[]): { label: string; badge: 'open' | 'closed' | 'unknown' } | undefined {
    const rawStatus = this.getFirstPropertyValue(featureProperties, [
      'estado_riego',
      'estado_toma',
      'estado',
      'toma_abierta',
      'abierta',
      'abierto',
      'is_open',
      'open'
    ]);

    if (rawStatus === undefined || rawStatus === null || rawStatus === '') {
      return undefined;
    }

    if (typeof rawStatus === 'boolean') {
      return rawStatus
        ? { label: 'Abierto', badge: 'open' }
        : { label: 'Cerrado', badge: 'closed' };
    }

    const normalizedStatus = this.normalizePropertyName(String(rawStatus));

    if (['1', 'true', 'si', 'yes', 'open', 'abierto', 'abierta'].includes(normalizedStatus)) {
      return { label: 'Abierto', badge: 'open' };
    }

    if (['0', 'false', 'no', 'closed', 'cerrado', 'cerrada'].includes(normalizedStatus)) {
      return { label: 'Cerrado', badge: 'closed' };
    }

    return { label: 'Sin datos', badge: 'unknown' };
  }

  private getFirstPropertyText(featureProperties: Record<string, unknown>[], aliases: string[]): string {
    const value = this.getFirstPropertyValue(featureProperties, aliases);
    if (value === undefined || value === null) {
      return '';
    }

    return String(value).trim();
  }

  private getFirstPropertyValue(featureProperties: Record<string, unknown>[], aliases: string[]): unknown {
    const normalizedAliases = aliases.map((alias) => this.normalizePropertyName(alias));

    for (const properties of featureProperties) {
      const entry = Object.entries(properties).find(([key, value]) =>
        value !== undefined &&
        value !== null &&
        String(value).trim() !== '' &&
        normalizedAliases.includes(this.normalizePropertyName(key))
      );

      if (entry) {
        return entry[1];
      }
    }

    return undefined;
  }

  private normalizePropertyName(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .toLowerCase();
  }

  private formatMeasuredValue(value: string, unit: string): string {
    if (!value) {
      return '';
    }

    return /[a-zA-Z]/.test(value) ? value : `${value} ${unit}`;
  }

  private isZeroMeasuredValue(value: string): boolean {
    const numericValue = Number(value
      .replace(',', '.')
      .replace(/[^0-9.-]/g, ''));

    return Number.isFinite(numericValue) && numericValue === 0;
  }

  private formatHectares(value: string): string {
    if (!value) {
      return '';
    }

    return /[a-zA-Z]/.test(value) ? value : `${value} ha`;
  }

  private formatTakeText(value: string): string {
    const trimmedValue = value.trim();
    if (/^toma\b/i.test(trimmedValue)) {
      return trimmedValue;
    }

    return /^\d+$/.test(trimmedValue) ? `Toma nº${trimmedValue}` : `Toma ${trimmedValue}`;
  }
}
